import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getUserById, updateUser } from "../../../api/userApi";

const UpdateUser = ({ userId, isOpen, onClose, onUpdate }) => {
  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    NIC: "",
    email: "",
  });
  const [originalData, setOriginalData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch user data when modal opens
  useEffect(() => {
    if (isOpen && userId) {
      fetchUserData(userId);
    }
  }, [isOpen, userId]);

  // Fetch user data from API
  const fetchUserData = async (id) => {
    try {
      setIsLoading(true);
      const data = await getUserById(id);
      setUserData({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        phone_number: data.phone_number || "",
        NIC: data.NIC || "",
        email: data.email || "",
      });
      setOriginalData(data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching user:", error);
      toast.error("Failed to load user data");
      setIsLoading(false);
      onClose();
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });

    // Clear validation error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};

    if (!userData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!userData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (!userData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(userData.phone_number)) {
      newErrors.phone_number = "Phone number must be 10 digits";
    }

    if (userData.email && !/\S+@\S+\.\S+/.test(userData.email)) {
      newErrors.email = "Invalid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);

      // Only send changed fields
      const updateData = {};
      Object.keys(userData).forEach((key) => {
        if (userData[key] !== originalData[key]) {
          updateData[key] = userData[key];
        }
      });

      // If nothing changed, just close the modal
      if (Object.keys(updateData).length === 0) {
        toast.info("No changes detected");
        onClose();
        return;
      }

      const result = await updateUser(userId, updateData);
      toast.success("User updated successfully!");
      onUpdate(result.user); // Pass updated user back to parent component
      onClose();
    } catch (error) {
      console.error("Error updating user:", error);
      // Special handling for specific error messages
      if (error.message && error.message.includes("phone")) {
        setErrors({
          ...errors,
          phone_number: "Phone number already registered",
        });
        toast.error("Phone number already registered");
      } else if (
        error.message &&
        error.message.includes("NIC already registered")
      ) {
        setErrors({
          ...errors,
          NIC: "NIC already registered",
        });
        toast.error("NIC already registered");
      } else {
        toast.error(error.message || "Failed to update user");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
          <h2 className="text-xl font-semibold text-gray-800">Update User Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-1"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-500 text-sm">Loading user data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-1">
            <div className="grid grid-cols-2 gap-4">
              {/* First Name */}
              <div className="mb-5">
                <label
                  htmlFor="first_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  First Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={userData.first_name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 bg-gray-50 border rounded-md shadow-sm focus:ring-2 focus:outline-none transition-colors
                      ${
                        errors.first_name
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-100"
                      }
                    `}
                  />
                </div>
                {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
              </div>

              {/* Last Name */}
              <div className="mb-5">
                <label
                  htmlFor="last_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Last Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={userData.last_name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 bg-gray-50 border rounded-md shadow-sm focus:ring-2 focus:outline-none transition-colors
                      ${
                        errors.last_name
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-100"
                      }
                    `}
                  />
                </div>
                {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
              </div>
            </div>

            {/* Phone Number */}
            <div className="mb-5">
              <label
                htmlFor="phone_number"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="phone_number"
                  name="phone_number"
                  value={userData.phone_number}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 bg-gray-50 border rounded-md shadow-sm focus:ring-2 focus:outline-none transition-colors
                    ${
                      errors.phone_number
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200 focus:border-blue-500 focus:ring-blue-100"
                    }
                  `}
                />
              </div>
              {errors.phone_number && <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>}
            </div>

            {/* NIC */}
            <div className="mb-5">
              <label
                htmlFor="NIC"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                NIC (National ID)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="NIC"
                  name="NIC"
                  value={userData.NIC}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 bg-gray-50 border rounded-md shadow-sm focus:ring-2 focus:outline-none transition-colors
                    ${
                      errors.NIC
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200 focus:border-blue-500 focus:ring-blue-100"
                    }
                  `}
                />
              </div>
              {errors.NIC && <p className="text-red-500 text-xs mt-1">{errors.NIC}</p>}
            </div>

            {/* Email */}
            <div className="mb-5">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={userData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 bg-gray-50 border rounded-md shadow-sm focus:ring-2 focus:outline-none transition-colors
                    ${
                      errors.email
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200 focus:border-blue-500 focus:ring-blue-100"
                    }
                  `}
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div className="flex justify-end space-x-3 mt-8 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors font-medium"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors font-medium"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UpdateUser;